import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Plus, X, CheckCircle, Calendar, Users, Wrench, FileText, ChevronDown, ChevronUp, Package, Printer } from 'lucide-react';
import { horasEntre, horasExtraDe, fmtHoras, totalesPorPersona } from '../utils/horasObra';

const fmt = (n) => parseFloat(n||0).toLocaleString('es-EC',{minimumFractionDigits:2,maximumFractionDigits:2});
// Las categorías de gasto son texto libre (las mismas que el asesor predijo, o nuevas que
// el técnico agregue) — solo 'retiro' es una categoría fija reservada para el asesor.
const categoriaLabel = (cat) => cat === 'retiro' ? 'Retiro / Dinero entregado' : (cat || '—');
const badgeEstado = (e) => {
  const map = { asignada:'badge-yellow', en_progreso:'badge-blue', completada:'badge-green' };
  const label = { asignada:'Asignada', en_progreso:'En progreso', completada:'Completada' };
  return <span className={`badge ${map[e]||'badge-gray'}`}>{label[e]||e}</span>;
};

export default function ObraPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isTecnico = user?.rol === 'tecnico';
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);

  const load = async () => {
    try { const r = await api.get('/obras'); setObras(r.data); }
    catch { toast.error('Error al cargar obras'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Llega desde un clic en la Agenda compartida sobre esta obra
  useEffect(() => {
    if (location.state?.openObraId && obras.length > 0) {
      const o = obras.find(x => x.id === location.state.openObraId);
      if (o) { setSelected(o); setModal('detalle'); }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, obras]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Obras</h1>
          <p className="page-subtitle">{obras.length} obra(s) asignada(s)</p>
        </div>
      </div>

      {loading ? (
        <div style={{textAlign:'center',padding:40}}><div className="spinner spinner-dark" style={{width:30,height:30,margin:'0 auto'}}/></div>
      ) : obras.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Wrench size={48}/>
            <h3>Sin obras asignadas</h3>
            <p>{isTecnico ? 'Aún no tienes obras asignadas' : 'Asigna técnicos desde la sección de Proyectos'}</p>
          </div>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {obras.map(obra => (
            <ObraCard key={obra.id} obra={obra} isTecnico={isTecnico} onSelect={() => { setSelected(obra); setModal('detalle'); }}/>
          ))}
        </div>
      )}

      {modal === 'detalle' && selected && (
        isTecnico ? (
          <ObraDetalleModalTecnico obra={selected} onClose={() => { setModal(null); setSelected(null); }} onSaved={load}/>
        ) : (
          <ObraDetalleModalAsesor obra={selected} onClose={() => { setModal(null); setSelected(null); }} onSaved={load}/>
        )
      )}
    </div>
  );
}

// ── OBRA CARD ─────────────────────────────────────────────────────────────────
function ObraCard({ obra, isTecnico, onSelect }) {
  const proyecto = obra.project;
  const cliente = proyecto?.client?.empresa || 'Sin empresa';
  const tecnico = obra.tecnico ? `${obra.tecnico.nombre} ${obra.tecnico.apellido}` : '—';
  const asesor  = obra.asesor  ? `${obra.asesor.nombre} ${obra.asesor.apellido}` : '—';

  let diasTrabajo = [];
  try { diasTrabajo = JSON.parse(obra.diasTrabajo || '[]'); } catch {}

  const borderColor = { asignada:'#d97706', en_progreso:'#2563eb', completada:'#16a34a' }[obra.estado] || '#ccc';

  return (
    <div style={{background:'white',borderRadius:12,padding:'16px 20px',boxShadow:'var(--sombra)',borderLeft:`4px solid ${borderColor}`,cursor:'pointer'}}
      onClick={onSelect}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:4}}>
            <strong style={{fontSize:16}}>{proyecto?.referencia}</strong>
            <span className={`badge ${proyecto?.tipoVenta==='servicio'?'badge-blue':'badge-orange'}`}>{proyecto?.tipoVenta==='servicio'?'Servicio':'Bien'}</span>
            {badgeEstado(obra.estado)}
          </div>
          <div style={{fontSize:14,color:'#666',marginBottom:2}}>{proyecto?.nombre}</div>
          <div style={{fontSize:13,color:'#888'}}>{cliente}</div>
        </div>
        <div style={{textAlign:'right',fontSize:12,color:'#888'}}>
          {isTecnico ? (
            <div>Asignado por: <strong style={{color:'#7B0D0D'}}>{asesor}</strong></div>
          ) : (
            <div>Técnico: <strong style={{color:'#7B0D0D'}}>{tecnico}</strong></div>
          )}
        </div>
      </div>
      {diasTrabajo.length > 0 && (
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:8}}>
          {diasTrabajo.slice(0,4).map((d,i) => (
            <div key={i} style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:6,padding:'3px 8px',fontSize:11,color:'#0369a1'}}>
              <Calendar size={10} style={{marginRight:3}}/>{d.fecha} {d.horaInicio&&`${d.horaInicio}–${d.horaFin}`}
            </div>
          ))}
          {diasTrabajo.length > 4 && <span style={{fontSize:11,color:'#888'}}>+{diasTrabajo.length-4} más</span>}
        </div>
      )}
    </div>
  );
}

// ── Collapsible section helper ────────────────────────────────────────────────
function Seccion({ icon, title, color, bg, open, onToggle, children }) {
  return (
    <div style={{border:'1px solid #e5e5e5',borderRadius:10,overflow:'hidden',marginBottom:14}}>
      <button type="button" onClick={onToggle}
        style={{width:'100%',padding:'12px 16px',background:bg,border:'none',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:14,fontWeight:600,color}}>
        <span>{icon}{title}</span>
        {open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
      </button>
      {open && <div style={{padding:14}}>{children}</div>}
    </div>
  );
}

// ── MODAL TÉCNICO: sin datos financieros, solo lo operativo ───────────────────
function ObraDetalleModalTecnico({ obra, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [showDias, setShowDias] = useState(true);
  const [showRoster, setShowRoster] = useState(true);
  const [showProductos, setShowProductos] = useState(true);
  const [showGastos, setShowGastos] = useState(true);
  const [showAnotaciones, setShowAnotaciones] = useState(false);

  let initDias = [];
  try { initDias = JSON.parse(obra.diasTrabajo || '[]'); } catch {}
  const [dias, setDias] = useState(initDias);
  // Días ya guardados (existían al abrir el modal, o se guardaron en esta sesión) quedan bloqueados
  const [diasGuardadosCount, setDiasGuardadosCount] = useState(initDias.length);
  const [instrumentos, setInstrumentos] = useState(obra.instrumentos || '');
  const [notas, setNotas] = useState(obra.notas || '');
  const [estado, setEstado] = useState(obra.estado || 'asignada');

  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [savingPersonaId, setSavingPersonaId] = useState(null);

  const [gastos, setGastos] = useState([]);
  const [loadingGastos, setLoadingGastos] = useState(true);
  // Las categorías disponibles son las mismas que el asesor definió en la proyección de gastos
  // (obra.gastosPredichos[].concepto) — si el técnico necesita una que no está en la lista, la
  // escribe directamente en el mismo campo (datalist: sugiere pero no obliga).
  const categoriasDisponibles = [...new Set((obra.gastosPredichos||[]).map(g=>g.concepto).filter(Boolean))];
  const [gastoForm, setGastoForm] = useState({ categoria: categoriasDisponibles[0]||'', conFactura:false, fecha:new Date().toISOString().slice(0,10), detalle:'', numeroFactura:'', valor:'' });
  const [savingGasto, setSavingGasto] = useState(false);

  const productosCotizados = obra.project?.productosCotizados || [];
  const [consumos, setConsumos] = useState([]);
  const [loadingConsumos, setLoadingConsumos] = useState(true);
  const [consumoForm, setConsumoForm] = useState({ productoCodigo: productosCotizados[0]?.codigo || '', fecha:new Date().toISOString().slice(0,10), cantidad:'' });
  const [savingConsumo, setSavingConsumo] = useState(false);

  const loadRoster = async () => {
    setLoadingRoster(true);
    try { const r = await api.get(`/obras/${obra.id}/personas`); setRoster(r.data); }
    catch { toast.error('Error al cargar el personal'); }
    finally { setLoadingRoster(false); }
  };
  const loadGastos = async () => {
    setLoadingGastos(true);
    try { const r = await api.get(`/obras/${obra.id}/gastos`); setGastos(r.data.items||[]); }
    catch { toast.error('Error al cargar gastos'); }
    finally { setLoadingGastos(false); }
  };
  const loadConsumos = async () => {
    setLoadingConsumos(true);
    try { const r = await api.get(`/obras/${obra.id}/productos-consumo`); setConsumos(r.data); }
    catch { toast.error('Error al cargar consumo de productos'); }
    finally { setLoadingConsumos(false); }
  };
  useEffect(() => { loadRoster(); loadGastos(); loadConsumos(); /* eslint-disable-next-line */ }, [obra.id]);

  const updateRosterField = (idx, k, v) => setRoster(prev => prev.map((p,i)=>i===idx?{...p,[k]:v}:p));
  const saveIdentidad = async (idx) => {
    const p = roster[idx];
    setSavingPersonaId(p.id);
    try {
      await api.put(`/obras/${obra.id}/personas/${p.id}`, { nombreReal: p.nombreReal, cedula: p.cedula });
      toast.success('Identidad guardada');
    } catch(e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSavingPersonaId(null); }
  };

  const addDia = () => setDias(prev => [...prev, { fecha:'', horaInicio:'', horaFin:'', asistentes:[] }]);
  const removeDia = (i) => setDias(prev => prev.filter((_,idx)=>idx!==i));
  const updateDia = (i,k,v) => setDias(prev => prev.map((d,idx)=> idx===i ? {...d,[k]:v} : d));
  const toggleAsistente = (i, personaId) => setDias(prev => prev.map((d,idx) => {
    if (idx!==i) return d;
    const asistentes = d.asistentes || [];
    const existe = asistentes.some(a=>a.personaId===personaId);
    return {
      ...d,
      asistentes: existe
        ? asistentes.filter(a=>a.personaId!==personaId)
        : [...asistentes, { personaId, horaInicio: d.horaInicio, horaFin: d.horaFin }],
    };
  }));
  // Por defecto cada asistente hereda el horario del día completo, pero se puede ajustar
  // individualmente (ej. alguien que llegó tarde o se quedó horas extra distinto al resto).
  const updateAsistenteHora = (i, personaId, campo, valor) => setDias(prev => prev.map((d,idx) => {
    if (idx!==i) return d;
    return { ...d, asistentes: (d.asistentes||[]).map(a => a.personaId===personaId ? {...a,[campo]:valor} : a) };
  }));

  const handleGuardarBitacoraDiaria = async () => {
    setSaving(true);
    try {
      const payload = {
        diasTrabajo: JSON.stringify(dias),
        instrumentos, notas, estado,
        fechaCompletada: estado === 'completada' ? new Date().toISOString().split('T')[0] : null,
      };
      await api.put(`/obras/${obra.id}`, payload);
      toast.success('Obra actualizada');
      setDiasGuardadosCount(dias.length);
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleAddGasto = async () => {
    if (!gastoForm.categoria.trim()) { toast.error('Indica la categoría del gasto'); return; }
    if (!gastoForm.fecha || !gastoForm.valor) { toast.error('Completa fecha y valor'); return; }
    if (gastoForm.conFactura && !gastoForm.numeroFactura) { toast.error('Indica el número de factura'); return; }
    setSavingGasto(true);
    try {
      await api.post(`/obras/${obra.id}/gastos`, {
        categoria: gastoForm.categoria.trim(), conFactura: gastoForm.conFactura,
        fecha: gastoForm.fecha, detalle: gastoForm.detalle || null,
        numeroFactura: gastoForm.conFactura ? gastoForm.numeroFactura : null,
        valor: parseFloat(gastoForm.valor),
      });
      setGastoForm(p => ({ categoria: p.categoria, conFactura:false, fecha:new Date().toISOString().slice(0,10), detalle:'', numeroFactura:'', valor:'' }));
      toast.success('Gasto registrado');
      await loadGastos();
    } catch(e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSavingGasto(false); }
  };

  const handleDeleteGasto = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try { await api.delete(`/obras/${obra.id}/gastos/${id}`); toast.success('Eliminado'); await loadGastos(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleAddConsumo = async () => {
    if (!consumoForm.productoCodigo || !consumoForm.fecha || !consumoForm.cantidad) { toast.error('Completa producto, fecha y cantidad'); return; }
    setSavingConsumo(true);
    try {
      await api.post(`/obras/${obra.id}/productos-consumo`, {
        productoCodigo: consumoForm.productoCodigo, fecha: consumoForm.fecha, cantidad: parseFloat(consumoForm.cantidad),
      });
      setConsumoForm(p => ({ ...p, cantidad:'' }));
      toast.success('Consumo registrado');
      await loadConsumos();
    } catch(e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSavingConsumo(false); }
  };

  const handleDeleteConsumo = async (id) => {
    if (!window.confirm('¿Eliminar este registro de consumo?')) return;
    try { await api.delete(`/obras/${obra.id}/productos-consumo/${id}`); toast.success('Eliminado'); await loadConsumos(); }
    catch { toast.error('Error al eliminar'); }
  };

  const proyecto = obra.project;
  const cliente = proyecto?.client?.empresa || 'Sin empresa';
  const cotizacionAprobada = !!proyecto?.cotizacionAprobada;

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg" style={{maxWidth:720}}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Obra — {proyecto?.referencia}</h3>
            <div style={{fontSize:13,color:'#888',marginTop:2}}>{cliente} · {proyecto?.tipoVenta==='servicio'?'Servicio':'Bien'}</div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={18}/></button>
        </div>

        <div className="modal-body" style={{maxHeight:'72vh',overflowY:'auto'}}>
          <div style={{padding:'10px 14px',background:'#f8faff',borderRadius:8,border:'1px solid #bfdbfe',marginBottom:16,fontSize:13}}>
            <div style={{display:'flex',gap:20,flexWrap:'wrap'}}>
              <span>Proyecto: <strong>{proyecto?.referencia}</strong></span>
              <span>Tipo: <strong>{proyecto?.tipoVenta==='servicio'?'Servicio':'Bien'}</strong></span>
              {obra.asesor && <span>Asignado por: <strong style={{color:'#7B0D0D'}}>{obra.asesor.nombre} {obra.asesor.apellido}</strong></span>}
            </div>
          </div>

          {!cotizacionAprobada && (
            <div style={{fontSize:13,color:'#92400e',background:'#fffbeb',border:'1px solid #fef3c7',borderRadius:8,padding:'10px 14px',marginBottom:16,display:'flex',gap:8,alignItems:'flex-start'}}>
              <span style={{fontSize:16,lineHeight:1}}>⏳</span>
              <span>Esta obra todavía no se puede llenar — se habilita apenas el asesor apruebe una cotización de este proyecto. Cuando eso pase, vas a poder registrar días de trabajo, gastos, consumo de productos y completar la identidad del personal.</span>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Estado de la obra</label>
            <div style={{display:'flex',gap:8}}>
              {[{v:'asignada',l:'Asignada'},{v:'en_progreso',l:'En progreso'},{v:'completada',l:'Completada'}].map(s=>(
                <button key={s.v} type="button" disabled={!cotizacionAprobada} onClick={()=>setEstado(s.v)}
                  style={{padding:'8px 16px',borderRadius:8,border:`2px solid ${estado===s.v?'#7B0D0D':'#e5e5e5'}`,background:estado===s.v?'#7B0D0D':'white',color:estado===s.v?'white':'#555',cursor:cotizacionAprobada?'pointer':'not-allowed',opacity:cotizacionAprobada?1:0.5,fontSize:13,fontWeight:500}}>
                  {s.v==='completada'&&<CheckCircle size={14} style={{marginRight:4}}/>}{s.l}
                </button>
              ))}
            </div>
          </div>

          <Seccion icon={<Users size={15} style={{marginRight:8}}/>} title={`Personal — completar identidad (${roster.length})`}
            color="#0369a1" bg="#f0f9ff" open={showRoster} onToggle={()=>setShowRoster(!showRoster)}>
            {loadingRoster ? <div style={{textAlign:'center',padding:10}}><span className="spinner"/></div> :
             roster.length===0 ? <div style={{fontSize:12,color:'#aaa'}}>El asesor aún no definió personal para esta obra</div> : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {roster.map((p,idx)=>(
                  <div key={p.id} style={{display:'grid',gridTemplateColumns:'100px 1fr 140px auto',gap:8,alignItems:'center'}}>
                    <span style={{fontSize:12,color:'#888'}}>{p.alias}</span>
                    <input className="form-input" value={p.nombreReal||''} disabled={!cotizacionAprobada} onChange={e=>updateRosterField(idx,'nombreReal',e.target.value)} placeholder="Nombre completo"/>
                    <input className="form-input" value={p.cedula||''} disabled={!cotizacionAprobada} onChange={e=>updateRosterField(idx,'cedula',e.target.value)} placeholder="N° Cédula"/>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={()=>saveIdentidad(idx)} disabled={savingPersonaId===p.id||!cotizacionAprobada}>
                      {savingPersonaId===p.id?<span className="spinner"/>:'Guardar'}
                    </button>
                  </div>))}
              </div>)}
          </Seccion>

          <Seccion icon={<Calendar size={15} style={{marginRight:8}}/>} title={`Días de trabajo y asistencia (${dias.length})`}
            color="#0369a1" bg="#f0f9ff" open={showDias} onToggle={()=>setShowDias(!showDias)}>
            {dias.map((d,i) => {
              const guardado = i < diasGuardadosCount;
              return (
              <div key={i} style={{border:'1px solid '+(guardado?'#e5e5e5':'#eee'),borderRadius:8,padding:10,marginBottom:10,background:guardado?'#fafafa':'white'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr auto',gap:8,marginBottom:8,alignItems:'flex-end'}}>
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:11}}>Fecha</label>
                    <input className="form-input" type="date" value={d.fecha} disabled={guardado || !cotizacionAprobada} onChange={e=>updateDia(i,'fecha',e.target.value)}/>
                  </div>
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:11}}>Entrada</label>
                    <input className="form-input" type="time" value={d.horaInicio} disabled={guardado || !cotizacionAprobada} onChange={e=>updateDia(i,'horaInicio',e.target.value)}/>
                  </div>
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:11}}>Salida</label>
                    <input className="form-input" type="time" value={d.horaFin} disabled={guardado || !cotizacionAprobada} onChange={e=>updateDia(i,'horaFin',e.target.value)}/>
                  </div>
                  {guardado ? (
                    <span style={{fontSize:11,color:'#15803d',fontWeight:600,padding:8,marginBottom:2}}>Guardado</span>
                  ) : (
                    <button type="button" onClick={()=>removeDia(i)} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626',padding:8,marginBottom:2}}>
                      <X size={14}/>
                    </button>
                  )}
                </div>
                {roster.length>0 && (
                  <div>
                    <div style={{fontSize:11,color:'#888',marginBottom:4}}>Asistencia — por defecto usa el horario del día, pero se puede ajustar por persona (ej. alguien que llegó tarde o se quedó horas extra)</div>
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      {roster.map(p=>{
                        const asistente = (d.asistentes||[]).find(a=>a.personaId===p.id);
                        const presente = !!asistente;
                        const hi = asistente ? (asistente.horaInicio ?? d.horaInicio) : '';
                        const hf = asistente ? (asistente.horaFin ?? d.horaFin) : '';
                        const horas = presente ? horasEntre(hi, hf) : 0;
                        const extra = horasExtraDe(horas);
                        return (
                          <div key={p.id} style={{display:'flex',alignItems:'center',gap:8,fontSize:12,padding:'6px 8px',background:presente?'#f0fdf4':'#fafafa',borderRadius:6}}>
                            <label style={{display:'flex',alignItems:'center',gap:6,cursor:guardado?'default':'pointer',opacity:guardado?0.7:1,minWidth:150}}>
                              <input type="checkbox" checked={presente} disabled={guardado || !cotizacionAprobada} onChange={()=>toggleAsistente(i,p.id)}/>
                              {p.nombreReal || p.alias}
                            </label>
                            {presente && (<>
                              <input type="time" className="form-input" value={hi} disabled={guardado || !cotizacionAprobada}
                                onChange={e=>updateAsistenteHora(i,p.id,'horaInicio',e.target.value)}
                                style={{width:100,padding:'4px 6px',fontSize:11}} title="Hora de entrada de esta persona"/>
                              <span style={{color:'#aaa'}}>–</span>
                              <input type="time" className="form-input" value={hf} disabled={guardado || !cotizacionAprobada}
                                onChange={e=>updateAsistenteHora(i,p.id,'horaFin',e.target.value)}
                                style={{width:100,padding:'4px 6px',fontSize:11}} title="Hora de salida de esta persona"/>
                              {horas>0 && (
                                <span style={{fontSize:11,color:'#555'}}>
                                  {fmtHoras(horas)}
                                  {extra>0 && <strong style={{color:'#b45309'}}> · +{fmtHoras(extra)} extra</strong>}
                                </span>
                              )}
                            </>)}
                          </div>);
                      })}
                    </div>
                  </div>)}
              </div>
            );})}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addDia} disabled={!cotizacionAprobada}>
              <Plus size={14}/> Agregar día
            </button>
          </Seccion>

          <Seccion icon={<Package size={15} style={{marginRight:8}}/>} title={`Productos — consumo en obra (${productosCotizados.length})`}
            color="#0369a1" bg="#f0f9ff" open={showProductos} onToggle={()=>setShowProductos(!showProductos)}>
            {productosCotizados.length===0 ? (
              <div style={{fontSize:12,color:'#aaa'}}>Aún no hay una cotización aprobada con productos para este proyecto</div>
            ) : (
              <>
                <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:14}}>
                  {productosCotizados.map(p=>{
                    const consumido = consumos.filter(c=>c.productoCodigo===p.codigo).reduce((s,c)=>s+Number(c.cantidad||0),0);
                    const restante = Number(p.cantidad||0) - consumido;
                    return (
                      <div key={p.codigo} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,padding:'8px 12px',background:'#fafafa',borderRadius:6}}>
                        <span><strong style={{color:'#7B0D0D'}}>{p.codigo}</strong> — {p.nombre}{p.presentacion&&<span style={{color:'#888'}}> ({p.presentacion})</span>}</span>
                        <span style={{display:'flex',gap:14,fontSize:12}}>
                          <span>Cotizado: <strong>{p.cantidad}</strong></span>
                          <span style={{color:'#0369a1'}}>Consumido: <strong>{consumido} {p.unidadMedida}</strong></span>
                          <span style={{color:restante<0?'#dc2626':'#15803d'}}>Restante: <strong>{restante} {p.unidadMedida}</strong></span>
                        </span>
                      </div>);
                  })}
                </div>

                <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12,alignItems:'flex-end'}}>
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:11}}>Producto</label>
                    <select className="form-select" value={consumoForm.productoCodigo} onChange={e=>setConsumoForm(p=>({...p,productoCodigo:e.target.value}))}>
                      {productosCotizados.map(p=><option key={p.codigo} value={p.codigo}>{p.codigo} — {p.nombre}{p.presentacion?` (${p.presentacion})`:''}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:11}}>Fecha</label>
                    <input className="form-input" type="date" value={consumoForm.fecha} onChange={e=>setConsumoForm(p=>({...p,fecha:e.target.value}))}/>
                  </div>
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:11}}>Cantidad usada</label>
                    <input className="form-input" style={{width:110}} type="number" step="any" min="0" value={consumoForm.cantidad} onChange={e=>setConsumoForm(p=>({...p,cantidad:e.target.value}))}/>
                  </div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleAddConsumo} disabled={savingConsumo}>
                    {savingConsumo?<span className="spinner"/>:<><Plus size={13}/> Registrar</>}
                  </button>
                </div>

                {loadingConsumos ? <div style={{textAlign:'center'}}><span className="spinner"/></div> :
                 consumos.length===0 ? <div style={{fontSize:12,color:'#aaa'}}>Aún no has registrado consumo</div> : (
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {consumos.map(c=>{
                      const prod = productosCotizados.find(p=>p.codigo===c.productoCodigo);
                      return (
                        <div key={c.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,padding:'6px 10px',background:'#fafafa',borderRadius:6}}>
                          <span><span className="badge badge-blue" style={{marginRight:6}}>{c.productoCodigo}</span>{c.fecha} — {prod?.nombre||c.productoCodigo}{prod?.presentacion&&<span style={{color:'#888'}}> ({prod.presentacion})</span>}</span>
                          <span style={{display:'flex',alignItems:'center',gap:10}}>
                            <strong>{c.cantidad} {prod?.unidadMedida}</strong>
                            <button type="button" onClick={()=>handleDeleteConsumo(c.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626'}}><X size={13}/></button>
                          </span>
                        </div>);
                    })}
                  </div>)}
              </>
            )}
          </Seccion>

          <Seccion icon={<FileText size={15} style={{marginRight:8}}/>} title="Instrumentos y notas"
            color="#92400e" bg="#fffbeb" open={showAnotaciones} onToggle={()=>setShowAnotaciones(!showAnotaciones)}>
            <div className="form-group">
              <label className="form-label"><Wrench size={13} style={{marginRight:4}}/>Lista de instrumentos / materiales</label>
              <textarea className="form-textarea" rows={3} value={instrumentos} disabled={!cotizacionAprobada} onChange={e=>setInstrumentos(e.target.value)}
                placeholder="Ej: Pistola de pintura x2, Compresor 5HP, Rodillos, Mascarillas..."/>
            </div>
            <div className="form-group">
              <label className="form-label">Notas adicionales</label>
              <textarea className="form-textarea" rows={3} value={notas} disabled={!cotizacionAprobada} onChange={e=>setNotas(e.target.value)}
                placeholder="Observaciones de la obra..."/>
            </div>
          </Seccion>

          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
            <button type="button" className="btn btn-primary" onClick={handleGuardarBitacoraDiaria} disabled={saving||!cotizacionAprobada}>
              {saving ? <><span className="spinner"/> Guardando...</> : 'Guardar estado / días / notas'}
            </button>
          </div>

          <Seccion icon={<FileText size={15} style={{marginRight:8}}/>} title="Mis gastos de obra"
            color="#7B0D0D" bg="#fff5f5" open={showGastos} onToggle={()=>setShowGastos(!showGastos)}>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12,alignItems:'flex-end'}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label" style={{fontSize:11}}>Categoría</label>
                <input className="form-input" list="categorias-gasto-tecnico" value={gastoForm.categoria}
                  onChange={e=>setGastoForm(p=>({...p,categoria:e.target.value}))}
                  placeholder={categoriasDisponibles.length?'Elige o escribe una nueva':'Ej: Materiales'}/>
                <datalist id="categorias-gasto-tecnico">
                  {categoriasDisponibles.map(c=><option key={c} value={c}/>)}
                </datalist>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label" style={{fontSize:11}}>Fecha</label>
                <input className="form-input" type="date" value={gastoForm.fecha} onChange={e=>setGastoForm(p=>({...p,fecha:e.target.value}))}/>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,paddingBottom:8}}>
                <input type="checkbox" checked={gastoForm.conFactura} onChange={e=>setGastoForm(p=>({...p,conFactura:e.target.checked}))}/>
                Con factura
              </label>
              {gastoForm.conFactura ? (
                <div className="form-group" style={{margin:0}}>
                  <label className="form-label" style={{fontSize:11}}>N° Factura</label>
                  <input className="form-input" style={{width:120}} value={gastoForm.numeroFactura} onChange={e=>setGastoForm(p=>({...p,numeroFactura:e.target.value}))}/>
                </div>
              ) : (
                <div className="form-group" style={{margin:0,flex:1}}>
                  <label className="form-label" style={{fontSize:11}}>Detalle</label>
                  <input className="form-input" value={gastoForm.detalle} onChange={e=>setGastoForm(p=>({...p,detalle:e.target.value}))} placeholder="Ej: Almuerzo del equipo"/>
                </div>
              )}
              <div className="form-group" style={{margin:0}}>
                <label className="form-label" style={{fontSize:11}}>Valor ($)</label>
                <input className="form-input" style={{width:100}} type="number" step="0.01" value={gastoForm.valor} onChange={e=>setGastoForm(p=>({...p,valor:e.target.value}))}/>
              </div>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleAddGasto} disabled={savingGasto||!cotizacionAprobada}>
                {savingGasto?<span className="spinner"/>:<><Plus size={13}/> Registrar</>}
              </button>
            </div>
            {!cotizacionAprobada && <div style={{fontSize:11,color:'#92400e',marginTop:-6,marginBottom:10}}>Podrás registrar gastos apenas se apruebe una cotización de este proyecto.</div>}

            {loadingGastos ? <div style={{textAlign:'center'}}><span className="spinner"/></div> :
             gastos.length===0 ? <div style={{fontSize:12,color:'#aaa'}}>Aún no has registrado gastos</div> : (
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {gastos.map(g=>(
                  <div key={g.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,padding:'6px 10px',background:'#fafafa',borderRadius:6}}>
                    <span>
                      <span className={`badge ${g.conFactura?'badge-blue':'badge-gray'}`} style={{marginRight:6}}>{categoriaLabel(g.categoria)}</span>
                      {g.fecha} — {g.conFactura ? `Factura ${g.numeroFactura}` : (g.detalle||'—')}
                    </span>
                    <span style={{display:'flex',alignItems:'center',gap:10}}>
                      <strong>${fmt(g.valor)}</strong>
                      <button type="button" onClick={()=>handleDeleteGasto(g.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626'}}><X size={13}/></button>
                    </span>
                  </div>))}
              </div>)}
          </Seccion>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── VISTA IMPRIMIBLE: bitácora completa de la obra ─────────────────────────────
function ObraBitacoraPrint({ obra, roster, gastos, totales, dias, consumos, onClose }) {
  const proyecto = obra.project;
  const cliente = proyecto?.client?.empresa || 'Sin empresa';
  const tecnico = obra.tecnico ? `${obra.tecnico.nombre} ${obra.tecnico.apellido}` : '—';
  const asesor = obra.asesor ? `${obra.asesor.nombre} ${obra.asesor.apellido}` : '—';
  const productosCotizados = proyecto?.productosCotizados || [];
  const gastosOperativos = (gastos || []).filter(g => g.categoria !== 'retiro');
  const retiros = (gastos || []).filter(g => g.categoria === 'retiro');
  const categoriasUsadas = [...new Set(gastosOperativos.map(g => g.categoria))];
  const resumenPorCategoria = categoriasUsadas.map(cat => {
    const items = gastosOperativos.filter(g => g.categoria === cat);
    const conFactura = items.filter(g => g.conFactura).reduce((s,g)=>s+Number(g.valor||0),0);
    const sinFactura = items.filter(g => !g.conFactura).reduce((s,g)=>s+Number(g.valor||0),0);
    return { label: cat, conFactura, sinFactura, total: conFactura+sinFactura };
  });
  const totalConFacturaGeneral = resumenPorCategoria.reduce((s,r)=>s+r.conFactura,0);
  const totalSinFacturaGeneral = resumenPorCategoria.reduce((s,r)=>s+r.sinFactura,0);
  const totalGeneralGastos = totalConFacturaGeneral + totalSinFacturaGeneral;

  const nombrePersona = (personaId) => {
    const p = roster.find(x => x.id === personaId);
    return p ? (p.nombreReal || p.alias) : '—';
  };

  const consumosConValor = (consumos || []).map(c => {
    const prod = productosCotizados.find(p => p.codigo === c.productoCodigo);
    const vUnitario = Number(prod?.vUnitario || 0);
    return { ...c, nombre: prod?.nombre || c.productoCodigo, unidadMedida: prod?.unidadMedida || '', vUnitario, valor: Number(c.cantidad || 0) * vUnitario };
  });
  const totalConsumo = consumosConValor.reduce((s, c) => s + c.valor, 0);

  const handlePrintAction = () => {
    const printArea = document.getElementById('bitacora-print-area');
    if (!printArea) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>Bitacora de obra - ${proyecto?.referencia||''}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:Arial,sans-serif;font-size:11px;background:white}
        table{width:100%;border-collapse:collapse;page-break-inside:auto;margin-bottom:14px}
        th,td{border:1px solid #ddd;padding:5px 8px;text-align:left}
        th{background:#f5f5f5;font-size:10px;text-transform:uppercase;color:#555}
        tr{page-break-inside:avoid}thead{display:table-header-group}
        h2{font-size:13px;color:#7B0D0D;margin:14px 0 6px;border-bottom:2px solid #7B0D0D;padding-bottom:4px}
        img{max-width:100%}
        @page{margin:1cm;size:A4}
      </style></head><body>${printArea.innerHTML}</body></html>`);
    win.document.close();
    win.onload = () => win.print();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:2000,overflowY:'auto',padding:'20px'}}>
      <div style={{maxWidth:820,margin:'0 auto',background:'white',borderRadius:8,overflow:'hidden'}}>
        <div style={{display:'flex',gap:10,padding:'12px 20px',background:'#1a1a1a',alignItems:'center'}}>
          <button type="button" className="btn btn-primary btn-sm" onClick={handlePrintAction}>Imprimir / PDF</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Cerrar</button>
        </div>

        <div id="bitacora-print-area" style={{fontFamily:'Arial,sans-serif',fontSize:11,padding:'24px 30px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14,borderBottom:'3px solid #7B0D0D',paddingBottom:14}}>
            <div>
              <img src="/images/logo-adrantaleh.png" alt="Adrantaleh" style={{maxHeight:60,maxWidth:240,objectFit:'contain',marginBottom:6}} onError={e=>{e.target.style.display='none';}}/>
              <div style={{fontSize:15,fontWeight:800,color:'#7B0D0D'}}>BITÁCORA DE OBRA</div>
            </div>
            <div style={{textAlign:'right',fontSize:11}}>
              <div><strong>Proyecto:</strong> {proyecto?.referencia}</div>
              <div><strong>Empresa:</strong> {cliente}</div>
              <div><strong>Técnico:</strong> {tecnico}</div>
              <div><strong>Asignado por:</strong> {asesor}</div>
              <div><strong>Estado:</strong> {obra.estado}</div>
              <div><strong>Fecha impresión:</strong> {new Date().toISOString().slice(0,10)}</div>
            </div>
          </div>

          <h2>Días de trabajo y asistencia ({(dias||[]).length})</h2>
          {(dias||[]).length===0 ? <div style={{fontSize:11,color:'#888',marginBottom:14}}>Sin días registrados.</div> : (
            <table>
              <thead><tr><th>Fecha</th><th>Persona</th><th>Entrada</th><th>Salida</th><th>Horas</th><th>Horas extra</th></tr></thead>
              <tbody>
                {dias.flatMap((d,i)=>{
                  const asistentes = d.asistentes || [];
                  if (asistentes.length === 0) {
                    return [<tr key={i}><td>{d.fecha}</td><td colSpan={5} style={{color:'#888'}}>Sin personal presente</td></tr>];
                  }
                  return asistentes.map((a,j)=>{
                    const hi = a.horaInicio ?? d.horaInicio;
                    const hf = a.horaFin ?? d.horaFin;
                    const horas = horasEntre(hi, hf);
                    const extra = horasExtraDe(horas);
                    return (
                      <tr key={`${i}-${j}`}>
                        {j===0 && <td rowSpan={asistentes.length}>{d.fecha}</td>}
                        <td>{nombrePersona(a.personaId)}</td>
                        <td>{hi||'—'}</td><td>{hf||'—'}</td>
                        <td>{horas>0?fmtHoras(horas):'—'}</td>
                        <td style={extra>0?{color:'#b45309',fontWeight:700}:{}}>{extra>0?fmtHoras(extra):'—'}</td>
                      </tr>);
                  });
                })}
              </tbody>
            </table>)}

          {(() => {
            const idsConHoras = [...new Set((dias||[]).flatMap(d=>(d.asistentes||[]).map(a=>a.personaId)))];
            const resumen = idsConHoras.map(id => ({ id, ...totalesPorPersona(dias, id) })).filter(r=>r.diasTrabajados>0);
            if (resumen.length === 0) return null;
            return (<>
              <h2>Resumen de horas por persona (jornada estándar: {8}h/día)</h2>
              <table>
                <thead><tr><th>Persona</th><th>Días trabajados</th><th>Horas totales</th><th>Horas extra totales</th></tr></thead>
                <tbody>
                  {resumen.map(r=>(
                    <tr key={r.id}>
                      <td>{nombrePersona(r.id)}</td>
                      <td>{r.diasTrabajados}</td>
                      <td>{fmtHoras(r.horas)}</td>
                      <td style={r.extra>0?{color:'#b45309',fontWeight:700}:{}}>{r.extra>0?fmtHoras(r.extra):'—'}</td>
                    </tr>))}
                </tbody>
              </table>
            </>);
          })()}

          <h2>Personal asignado ({roster.length})</h2>
          {roster.length===0 ? <div style={{fontSize:11,color:'#888',marginBottom:14}}>Sin personal registrado.</div> : (
            <table>
              <thead><tr><th>Alias</th><th>Nombre real</th><th>Cédula</th><th>Valor/día</th></tr></thead>
              <tbody>
                {roster.map(p=>(
                  <tr key={p.id}><td>{p.alias}</td><td>{p.nombreReal||'—'}</td><td>{p.cedula||'—'}</td><td>${fmt(p.valorDia)}</td></tr>))}
              </tbody>
            </table>)}

          <h2>Consumo de productos ({consumosConValor.length})</h2>
          {consumosConValor.length===0 ? <div style={{fontSize:11,color:'#888',marginBottom:14}}>Sin consumo registrado.</div> : (
            <table>
              <thead><tr><th>Fecha</th><th>Código</th><th>Producto</th><th>Cantidad</th><th>V. Unitario</th><th>Valor</th></tr></thead>
              <tbody>
                {consumosConValor.map(c=>(
                  <tr key={c.id}>
                    <td>{c.fecha}</td><td>{c.productoCodigo}</td><td>{c.nombre}</td>
                    <td>{c.cantidad} {c.unidadMedida}</td><td>${fmt(c.vUnitario)}</td><td>${fmt(c.valor)}</td>
                  </tr>))}
                <tr><td colSpan={5} style={{textAlign:'right',fontWeight:700}}>Total</td><td style={{fontWeight:700}}>${fmt(totalConsumo)}</td></tr>
              </tbody>
            </table>)}

          <h2>Gastos de obra</h2>
          {totales && (
            <div style={{display:'flex',gap:14,marginBottom:10,fontSize:11}}>
              <div><strong>Retiros:</strong> ${fmt(totales.totalRetiros)}</div>
              <div><strong>Facturado:</strong> ${fmt(totales.facturadoTotal)}</div>
              <div><strong>Sin factura:</strong> ${fmt(totales.sinFacturaTotal)}</div>
              <div><strong>Saldo:</strong> ${fmt(totales.saldo)}</div>
            </div>)}
          <div style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:4}}>RESUMEN POR CATEGORÍA</div>
          <table>
            <thead><tr><th>Categoría</th><th>Con factura</th><th>Sin factura</th><th>Total</th></tr></thead>
            <tbody>
              {resumenPorCategoria.map(r=>(
                <tr key={r.label}>
                  <td>{r.label}</td><td>${fmt(r.conFactura)}</td><td>${fmt(r.sinFactura)}</td><td>${fmt(r.total)}</td>
                </tr>))}
              <tr style={{fontWeight:700}}>
                <td>TOTAL</td><td>${fmt(totalConFacturaGeneral)}</td><td>${fmt(totalSinFacturaGeneral)}</td><td>${fmt(totalGeneralGastos)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:4}}>RETIROS ({retiros.length})</div>
          {retiros.length===0 ? <div style={{fontSize:11,color:'#888',marginBottom:10}}>Sin retiros.</div> : (
            <table>
              <thead><tr><th>Fecha</th><th>Detalle</th><th>Valor</th></tr></thead>
              <tbody>{retiros.map(g=>(<tr key={g.id}><td>{g.fecha}</td><td>{g.detalle||'—'}</td><td>${fmt(g.valor)}</td></tr>))}</tbody>
            </table>)}
          <div style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:4}}>GASTOS CON/SIN FACTURA ({gastosOperativos.length})</div>
          {gastosOperativos.length===0 ? <div style={{fontSize:11,color:'#888',marginBottom:10}}>Sin gastos registrados.</div> : (
            <table>
              <thead><tr><th>Categoría</th><th>Fecha</th><th>Factura</th><th>Detalle</th><th>Valor</th></tr></thead>
              <tbody>
                {gastosOperativos.map(g=>(
                  <tr key={g.id}>
                    <td>{categoriaLabel(g.categoria)}</td><td>{g.fecha}</td>
                    <td>{g.conFactura?`N° ${g.numeroFactura}`:'Sin factura'}</td>
                    <td>{g.detalle||'—'}</td><td>${fmt(g.valor)}</td>
                  </tr>))}
              </tbody>
            </table>)}

          <h2>Instrumentos y notas</h2>
          <div style={{marginBottom:8}}><strong>Instrumentos / materiales:</strong> {obra.instrumentos || '—'}</div>
          <div><strong>Notas:</strong> {obra.notas || '—'}</div>
        </div>
      </div>
    </div>
  );
}

// ── MODAL ASESOR/GERENCIA: roster financiero, retiros, liquidación ────────────
function ObraDetalleModalAsesor({ obra, onClose, onSaved }) {
  const [diasEstimados, setDiasEstimados] = useState(obra.diasEstimados ?? '');
  const [notas, setNotas] = useState(obra.notas || '');
  const [saving, setSaving] = useState(false);
  const [fechaInicioObra, setFechaInicioObra] = useState(obra.project?.fechaInicioObra || '');
  const [fechaFinEstimada, setFechaFinEstimada] = useState(obra.project?.fechaFinEstimada || '');
  const [savingFechas, setSavingFechas] = useState(false);
  const cotizacionAprobada = !!obra.project?.cotizacionAprobada;

  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(true);

  const [gastosPredichos, setGastosPredichos] = useState([]);
  const [loadingGastosPredichos, setLoadingGastosPredichos] = useState(true);

  const [porcentajeUtilidad, setPorcentajeUtilidad] = useState(obra.porcentajeUtilidad ?? 20);
  const [cantidadUnidadCobro, setCantidadUnidadCobro] = useState(obra.cantidadUnidadCobro ?? '');
  const [unidadCobro, setUnidadCobro] = useState(obra.unidadCobro || '');
  const [savingUtilidad, setSavingUtilidad] = useState(false);

  const [gastos, setGastos] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loadingGastos, setLoadingGastos] = useState(true);
  const [retiroForm, setRetiroForm] = useState({ fecha:new Date().toISOString().slice(0,10), detalle:'', valor:'' });
  const [savingRetiro, setSavingRetiro] = useState(false);

  const [consumos, setConsumos] = useState([]);
  const [showPrint, setShowPrint] = useState(false);

  const loadRoster = async () => {
    setLoadingRoster(true);
    try { const r = await api.get(`/obras/${obra.id}/personas`); setRoster(r.data); }
    catch { toast.error('Error al cargar el personal'); }
    finally { setLoadingRoster(false); }
  };
  const loadGastos = async () => {
    setLoadingGastos(true);
    try { const r = await api.get(`/obras/${obra.id}/gastos`); setGastos(r.data.items||[]); setTotales(r.data.totales||null); }
    catch { toast.error('Error al cargar gastos'); }
    finally { setLoadingGastos(false); }
  };
  const loadGastosPredichos = async () => {
    setLoadingGastosPredichos(true);
    try { const r = await api.get(`/obras/${obra.id}/gastos-predichos`); setGastosPredichos(r.data); }
    catch { toast.error('Error al cargar predicción de gastos'); }
    finally { setLoadingGastosPredichos(false); }
  };
  const loadConsumos = async () => {
    try { const r = await api.get(`/obras/${obra.id}/productos-consumo`); setConsumos(r.data); }
    catch { /* silencioso: no bloquea el resto de la vista */ }
  };
  useEffect(() => { loadRoster(); loadGastos(); loadGastosPredichos(); loadConsumos(); /* eslint-disable-next-line */ }, [obra.id]);

  const addRosterRow = async () => {
    try {
      const r = await api.post(`/obras/${obra.id}/personas`, { alias: `Ayudante ${roster.length+1}`, valorDia: 0 });
      setRoster(prev => [...prev, r.data]);
    } catch(e) { toast.error(e.response?.data?.error || 'Error'); }
  };
  const updateRosterField = (idx, k, v) => setRoster(prev => prev.map((p,i)=>i===idx?{...p,[k]:v}:p));
  const saveRosterRow = async (idx) => {
    const p = roster[idx];
    try { await api.put(`/obras/${obra.id}/personas/${p.id}`, { alias:p.alias, valorDia: parseFloat(p.valorDia)||0 }); toast.success('Guardado'); }
    catch(e) { toast.error(e.response?.data?.error || 'Error'); }
  };
  const removeRosterRow = async (idx) => {
    const p = roster[idx];
    if (!window.confirm('¿Quitar a esta persona del roster?')) return;
    try { await api.delete(`/obras/${obra.id}/personas/${p.id}`); setRoster(prev => prev.filter((_,i)=>i!==idx)); }
    catch { toast.error('Error al eliminar'); }
  };

  const addGastoPredichoRow = () => {
    setGastosPredichos(prev => [...prev, { id:`tmp-${Date.now()}`, concepto:'', cantidad:1, unidad:'', valor:0, _nuevo:true }]);
  };
  const updateGastoPredichoField = (idx, k, v) => setGastosPredichos(prev => prev.map((g,i)=>i===idx?{...g,[k]:v}:g));
  const saveGastoPredichoRow = async (idx) => {
    const g = gastosPredichos[idx];
    if (!g.concepto) { toast.error('El concepto es requerido'); return; }
    try {
      if (g._nuevo) {
        const r = await api.post(`/obras/${obra.id}/gastos-predichos`, {
          concepto: g.concepto, cantidad: parseInt(g.cantidad)||0, unidad: g.unidad, valor: parseFloat(g.valor)||0,
        });
        setGastosPredichos(prev => prev.map((x,i)=>i===idx?r.data:x));
      } else {
        await api.put(`/obras/${obra.id}/gastos-predichos/${g.id}`, {
          concepto: g.concepto, cantidad: parseInt(g.cantidad)||0, unidad: g.unidad, valor: parseFloat(g.valor)||0,
        });
      }
      toast.success('Guardado');
    } catch(e) { toast.error(e.response?.data?.error || 'Error'); }
  };
  const removeGastoPredichoRow = async (idx) => {
    const g = gastosPredichos[idx];
    if (g._nuevo) { setGastosPredichos(prev => prev.filter((_,i)=>i!==idx)); return; }
    if (!window.confirm('¿Quitar este gasto predicho?')) return;
    try { await api.delete(`/obras/${obra.id}/gastos-predichos/${g.id}`); setGastosPredichos(prev => prev.filter((_,i)=>i!==idx)); }
    catch { toast.error('Error al eliminar'); }
  };

  const gastoProyectado = roster.filter(p=>p.activo!==false).reduce((s,p)=>s+(parseFloat(p.valorDia)||0),0) * (parseFloat(diasEstimados)||0);
  const sumaGastosPredichos = gastosPredichos.reduce((s,g)=>s+(parseInt(g.cantidad)||0)*(parseFloat(g.valor)||0),0);
  const totalProyectado = sumaGastosPredichos + gastoProyectado;
  const utilidadDinero = totalProyectado * ((parseFloat(porcentajeUtilidad)||0)/100);
  const valorPorUnidad = parseFloat(cantidadUnidadCobro) > 0 ? (totalProyectado + utilidadDinero) / parseFloat(cantidadUnidadCobro) : null;

  const handleGuardarUtilidad = async () => {
    if (parseFloat(porcentajeUtilidad) < 20) { toast.error('La utilidad no puede ser menor al 20%'); return; }
    setSavingUtilidad(true);
    try {
      await api.put(`/obras/${obra.id}`, {
        porcentajeUtilidad: parseFloat(porcentajeUtilidad),
        cantidadUnidadCobro: cantidadUnidadCobro!==''?parseFloat(cantidadUnidadCobro):null,
        unidadCobro,
      });
      toast.success('Análisis guardado');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSavingUtilidad(false); }
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      await api.put(`/obras/${obra.id}`, { diasEstimados: diasEstimados!==''?parseFloat(diasEstimados):null, notas });
      toast.success('Obra actualizada');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleGuardarFechas = async () => {
    setSavingFechas(true);
    try {
      await api.put(`/projects/${obra.projectId}`, {
        fechaInicioObra: fechaInicioObra || '',
        fechaFinEstimada: fechaFinEstimada || '',
      });
      toast.success('Fechas de obra guardadas');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al guardar fechas'); }
    finally { setSavingFechas(false); }
  };

  const handleRetiro = async () => {
    if (!retiroForm.detalle || !retiroForm.valor) { toast.error('Completa detalle y valor'); return; }
    setSavingRetiro(true);
    try {
      await api.post(`/obras/${obra.id}/gastos`, { categoria:'retiro', fecha:retiroForm.fecha, detalle:retiroForm.detalle, valor:parseFloat(retiroForm.valor) });
      setRetiroForm({ fecha:new Date().toISOString().slice(0,10), detalle:'', valor:'' });
      toast.success('Retiro registrado');
      await loadGastos();
    } catch(e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSavingRetiro(false); }
  };

  let dias = [];
  try { dias = JSON.parse(obra.diasTrabajo || '[]'); } catch {}

  const proyecto = obra.project;
  const cliente = proyecto?.client?.empresa || 'Sin empresa';
  const tecnico = obra.tecnico ? `${obra.tecnico.nombre} ${obra.tecnico.apellido}` : '—';

  const gastosOperativos = gastos.filter(g=>g.categoria!=='retiro');
  const retiros = gastos.filter(g=>g.categoria==='retiro');

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg" style={{maxWidth:760}}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Obra — {proyecto?.referencia}</h3>
            <div style={{fontSize:13,color:'#888',marginTop:2}}>{cliente} · Técnico: {tecnico}</div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setShowPrint(true)}>
              <Printer size={14}/> Imprimir bitácora
            </button>
            <button className="btn-icon" onClick={onClose}><X size={18}/></button>
          </div>
        </div>
        {showPrint && (
          <ObraBitacoraPrint obra={obra} roster={roster} gastos={gastos} totales={totales}
            dias={dias} consumos={consumos} onClose={()=>setShowPrint(false)}/>
        )}

        <div className="modal-body" style={{maxHeight:'72vh',overflowY:'auto'}}>
          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label">Días estimados de trabajo</label>
              <input className="form-input" type="number" min="0" value={diasEstimados} onChange={e=>setDiasEstimados(e.target.value)} placeholder="Ej: 10"/>
            </div>
            <div style={{padding:'10px 14px',background:'#f0fdf4',borderRadius:8,border:'1px solid #bbf7d0',display:'flex',flexDirection:'column',justifyContent:'center'}}>
              <span style={{fontSize:11,color:'#555',fontWeight:600}}>Gasto proyectado en mano de obra</span>
              <span style={{fontSize:18,fontWeight:800,color:'#15803d'}}>${fmt(gastoProyectado)}</span>
            </div>
          </div>

          <div style={{border:'1px solid #e5e5e5',borderRadius:10,padding:14,marginBottom:14,background:cotizacionAprobada?'white':'#fafafa'}}>
            <label className="form-label" style={{display:'block',marginBottom:cotizacionAprobada?10:6}}>
              <Calendar size={13} style={{marginRight:4}}/>Fechas de obra
            </label>
            {!cotizacionAprobada ? (
              <div style={{fontSize:12,color:'#aaa'}}>Se habilita cuando una cotización de este proyecto quede aprobada.</div>
            ) : (
              <>
                <div className="form-row form-row-2">
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:11}}>Fecha exacta de inicio</label>
                    <input className="form-input" type="date" value={fechaInicioObra} onChange={e=>setFechaInicioObra(e.target.value)}/>
                  </div>
                  <div className="form-group" style={{margin:0}}>
                    <label className="form-label" style={{fontSize:11}}>Fecha tentativa de fin</label>
                    <input className="form-input" type="date" value={fechaFinEstimada} onChange={e=>setFechaFinEstimada(e.target.value)}/>
                  </div>
                </div>
                <div style={{display:'flex',justifyContent:'flex-end',marginTop:10}}>
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleGuardarFechas} disabled={savingFechas}>
                    {savingFechas?<span className="spinner"/>:'Guardar fechas'}
                  </button>
                </div>
              </>
            )}
          </div>

          <div style={{border:'1px solid #e5e5e5',borderRadius:10,padding:14,marginBottom:14}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <label className="form-label" style={{margin:0}}><Users size={13} style={{marginRight:4}}/>Personal (PAX) — {roster.length}</label>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addRosterRow}><Plus size={13}/> Agregar</button>
            </div>
            {loadingRoster ? <div style={{textAlign:'center'}}><span className="spinner"/></div> :
             roster.length===0 ? <div style={{fontSize:12,color:'#aaa'}}>Sin personal agregado aún</div> : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {roster.map((p,idx)=>(
                  <div key={p.id} style={{display:'grid',gridTemplateColumns:'1fr 100px 1fr 100px auto',gap:8,alignItems:'center'}}>
                    <input className="form-input" value={p.alias} onChange={e=>updateRosterField(idx,'alias',e.target.value)} placeholder="Alias"/>
                    <input className="form-input" type="number" step="0.01" value={p.valorDia} onChange={e=>updateRosterField(idx,'valorDia',e.target.value)} placeholder="Valor/día"/>
                    <span style={{fontSize:12,color:'#888'}}>{p.nombreReal || '(sin identidad aún)'}</span>
                    <span style={{fontSize:12,color:'#888'}}>{p.cedula || ''}</span>
                    <div style={{display:'flex',gap:4}}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={()=>saveRosterRow(idx)}>Guardar</button>
                      <button type="button" onClick={()=>removeRosterRow(idx)} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626'}}><X size={14}/></button>
                    </div>
                  </div>))}
              </div>)}
          </div>

          <div style={{border:'1px solid #e5e5e5',borderRadius:10,padding:14,marginBottom:14}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <label className="form-label" style={{margin:0}}>Predicción de gastos</label>
              {!cotizacionAprobada && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={addGastoPredichoRow}><Plus size={13}/> Agregar</button>
              )}
            </div>
            {cotizacionAprobada && (
              <div style={{fontSize:12,color:'#92400e',background:'#fffbeb',border:'1px solid #fef3c7',borderRadius:8,padding:'8px 10px',marginBottom:10}}>
                Bloqueado: el proyecto ya tiene una cotización aprobada, así que la predicción de gastos ya no se puede editar.
              </div>
            )}
            {loadingGastosPredichos ? <div style={{textAlign:'center'}}><span className="spinner"/></div> :
             gastosPredichos.length===0 ? <div style={{fontSize:12,color:'#aaa'}}>Sin gastos predichos agregados aún</div> : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                <div style={{display:'grid',gridTemplateColumns: cotizacionAprobada?'2fr 70px 80px 90px 90px':'2fr 70px 80px 90px 90px 108px',gap:8,fontSize:11,color:'#888',fontWeight:600,padding:'0 2px'}}>
                  <span>CONCEPTO</span><span>CANT.</span><span>UNIDAD</span><span>VALOR UNIT.</span><span>TOTAL</span>{!cotizacionAprobada&&<span></span>}
                </div>
                {gastosPredichos.map((g,idx)=>(
                  <div key={g.id} style={{display:'grid',gridTemplateColumns: cotizacionAprobada?'2fr 70px 80px 90px 90px':'2fr 70px 80px 90px 90px 108px',gap:8,alignItems:'center'}}>
                    <input className="form-input" style={{minWidth:0}} value={g.concepto} readOnly={cotizacionAprobada} onChange={e=>updateGastoPredichoField(idx,'concepto',e.target.value)} placeholder="Concepto"/>
                    <input className="form-input" style={{minWidth:0}} type="number" step="1" min="0" value={g.cantidad} readOnly={cotizacionAprobada} onChange={e=>updateGastoPredichoField(idx,'cantidad',e.target.value)}/>
                    <input className="form-input" style={{minWidth:0}} value={g.unidad||''} readOnly={cotizacionAprobada} onChange={e=>updateGastoPredichoField(idx,'unidad',e.target.value)} placeholder="Unidad"/>
                    <input className="form-input" style={{minWidth:0}} type="number" step="0.01" value={g.valor} readOnly={cotizacionAprobada} onChange={e=>updateGastoPredichoField(idx,'valor',e.target.value)}/>
                    <span style={{fontSize:12,fontWeight:600}}>${fmt((parseInt(g.cantidad)||0)*(parseFloat(g.valor)||0))}</span>
                    {!cotizacionAprobada && (
                      <div style={{display:'flex',gap:4}}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={()=>saveGastoPredichoRow(idx)}>Guardar</button>
                        <button type="button" onClick={()=>removeGastoPredichoRow(idx)} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626'}}><X size={14}/></button>
                      </div>
                    )}
                  </div>))}
              </div>)}
          </div>

          <div style={{border:'1px solid #e5e5e5',borderRadius:10,padding:14,marginBottom:14}}>
            <label className="form-label" style={{display:'block',marginBottom:10}}>Análisis de obra</label>
            {cotizacionAprobada && (
              <div style={{fontSize:12,color:'#92400e',background:'#fffbeb',border:'1px solid #fef3c7',borderRadius:8,padding:'8px 10px',marginBottom:10}}>
                Bloqueado: el proyecto ya tiene una cotización aprobada, así que el análisis de obra ya no se puede editar.
              </div>
            )}
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:10}}>
              <span style={{color:'#555'}}>Total gastos proyectados (mano de obra + gastos)</span>
              <strong>${fmt(totalProyectado)}</strong>
            </div>
            <div className="form-row form-row-2">
              <div className="form-group" style={{margin:0}}>
                <label className="form-label" style={{fontSize:12}}>% de utilidad deseada (mínimo 20%)</label>
                <input className="form-input" type="number" step="0.5" min="20" value={porcentajeUtilidad} readOnly={cotizacionAprobada}
                  onChange={e=>setPorcentajeUtilidad(e.target.value)}/>
              </div>
              <div style={{padding:'10px 14px',background:'#f0fdf4',borderRadius:8,border:'1px solid #bbf7d0',display:'flex',flexDirection:'column',justifyContent:'center'}}>
                <span style={{fontSize:11,color:'#555',fontWeight:600}}>Utilidad ({parseFloat(porcentajeUtilidad)||0}%)</span>
                <span style={{fontSize:18,fontWeight:800,color:'#15803d'}}>${fmt(utilidadDinero)}</span>
              </div>
            </div>
            <div className="form-row form-row-2" style={{marginTop:10}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label" style={{fontSize:12}}>Cantidad (m², ml, etc.)</label>
                <input className="form-input" type="number" step="0.01" min="0" value={cantidadUnidadCobro} readOnly={cotizacionAprobada}
                  onChange={e=>setCantidadUnidadCobro(e.target.value)} placeholder="Ej: 120"/>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label" style={{fontSize:12}}>Unidad</label>
                <input className="form-input" value={unidadCobro} readOnly={cotizacionAprobada} onChange={e=>setUnidadCobro(e.target.value)} placeholder="m², ml, etc."/>
              </div>
            </div>
            {valorPorUnidad!=null && (
              <div style={{padding:'10px 14px',background:'#eff6ff',borderRadius:8,border:'1px solid #bfdbfe',marginTop:10,display:'flex',flexDirection:'column'}}>
                <span style={{fontSize:11,color:'#555',fontWeight:600}}>Valor a cobrar por {unidadCobro||'unidad'}</span>
                <span style={{fontSize:18,fontWeight:800,color:'#1d4ed8'}}>${fmt(valorPorUnidad)}</span>
                <span style={{fontSize:10,color:'#888',marginTop:2}}>(Total gastos + utilidad) ÷ cantidad</span>
              </div>
            )}
            {!cotizacionAprobada && (
              <div style={{display:'flex',justifyContent:'flex-end',marginTop:10}}>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleGuardarUtilidad} disabled={savingUtilidad}>
                  {savingUtilidad?<span className="spinner"/>:'Guardar análisis'}
                </button>
              </div>
            )}
          </div>

          {dias.length>0 && (
            <div style={{border:'1px solid #e5e5e5',borderRadius:10,padding:14,marginBottom:14}}>
              <label className="form-label"><Calendar size={13} style={{marginRight:4}}/>Días de trabajo registrados ({dias.length})</label>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {dias.map((d,i)=>(
                  <div key={i} style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:6,padding:'4px 10px',fontSize:12,color:'#0369a1'}}>
                    {d.fecha} {d.horaInicio&&`${d.horaInicio}–${d.horaFin}`} {d.asistentes?.length>0&&`· ${d.asistentes.length} presentes`}
                  </div>))}
              </div>
            </div>)}

          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea className="form-textarea" rows={2} value={notas} onChange={e=>setNotas(e.target.value)}/>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
            <button type="button" className="btn btn-primary" onClick={handleSaveInfo} disabled={saving}>
              {saving?<><span className="spinner"/> Guardando...</>:'Guardar días estimados / notas'}
            </button>
          </div>

          <div style={{border:'1px solid #e5e5e5',borderRadius:10,padding:14}}>
            <label className="form-label" style={{marginBottom:10,display:'block'}}><FileText size={13} style={{marginRight:4}}/>Bitácora de gastos</label>
            {totales && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:14}}>
                {[
                  ['Retiros', totales.totalRetiros, '#2563eb'],
                  ['Facturado', totales.facturadoTotal, '#7B0D0D'],
                  ['Sin factura', totales.sinFacturaTotal, '#92400e'],
                  ['Saldo', totales.saldo, totales.saldo>=0?'#15803d':'#dc2626'],
                ].map(([l,v,c])=>(
                  <div key={l} style={{padding:'8px 10px',background:'#fafafa',borderRadius:8,textAlign:'center'}}>
                    <div style={{fontSize:11,color:'#888'}}>{l}</div>
                    <div style={{fontSize:15,fontWeight:700,color:c}}>${fmt(v)}</div>
                  </div>))}
              </div>)}

            <div style={{padding:'12px 14px',background:'#f0f9ff',borderRadius:10,border:'1px solid #bae6fd',marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:600,marginBottom:8}}>Registrar retiro / dinero entregado</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                <input className="form-input" type="date" style={{width:140}} value={retiroForm.fecha} onChange={e=>setRetiroForm(p=>({...p,fecha:e.target.value}))}/>
                <input className="form-input" style={{flex:1,minWidth:180}} placeholder="Ej: Retiro de efectivo a Pedro Ruiz" value={retiroForm.detalle} onChange={e=>setRetiroForm(p=>({...p,detalle:e.target.value}))}/>
                <input className="form-input" style={{width:110}} type="number" step="0.01" placeholder="Valor" value={retiroForm.valor} onChange={e=>setRetiroForm(p=>({...p,valor:e.target.value}))}/>
                <button className="btn btn-primary btn-sm" onClick={handleRetiro} disabled={savingRetiro}>{savingRetiro?<span className="spinner"/>:'Registrar'}</button>
              </div>
            </div>

            {loadingGastos ? <div style={{textAlign:'center'}}><span className="spinner"/></div> : (
              <>
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:12,fontWeight:700,color:'#888',marginBottom:6}}>RETIROS ({retiros.length})</div>
                  {retiros.length===0 ? <div style={{fontSize:12,color:'#aaa'}}>Sin retiros registrados</div> : retiros.map(g=>(
                    <div key={g.id} style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 10px',background:'#fafafa',borderRadius:6,marginBottom:4}}>
                      <span>{g.fecha} — {g.detalle}</span>
                      <strong>${fmt(g.valor)}</strong>
                    </div>))}
                </div>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:'#888',marginBottom:6}}>GASTOS DEL TÉCNICO ({gastosOperativos.length})</div>
                  {gastosOperativos.length===0 ? <div style={{fontSize:12,color:'#aaa'}}>Sin gastos registrados aún</div> : gastosOperativos.map(g=>(
                    <div key={g.id} style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 10px',background:'#fafafa',borderRadius:6,marginBottom:4}}>
                      <span>
                        <span className={`badge ${g.conFactura?'badge-blue':'badge-gray'}`} style={{marginRight:6}}>{categoriaLabel(g.categoria)}</span>
                        {g.fecha} — {g.conFactura ? `Factura ${g.numeroFactura}` : (g.detalle||'—')}
                      </span>
                      <strong>${fmt(g.valor)}</strong>
                    </div>))}
                </div>
              </>)}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
